/** The two things the Google Drive connector is handed on every call: the
 * policy it writes onto an item, and the principal it writes it for. Both are
 * narrow enough to be checked here, on the control plane, before they cross
 * into the customer's deployment.
 */
import { StateError } from "@p0security/shared/types/error";

/** Ordered weakest first; {@link maxGoogleDriveAiRole} compares by index.
 *
 * Two roles, not Drive's five. Everything above `writer` buys capabilities P0's
 * Drive tool surface does not expose, so it would widen blast radius with no
 * matching capability — and a `writer` cannot trash the folder it was granted.
 */
export const GoogleDriveAiRoles = ["reader", "writer"] as const;
export type GoogleDriveAiRole = (typeof GoogleDriveAiRoles)[number];

/** The single policy that covers `roles`, or `undefined` when nothing is left
 * to grant.
 *
 * Drive permits exactly one permission entry per principal per item, so the
 * union across a request and its open siblings cannot become two policies — it
 * has to collapse to the strongest role any of them asked for.
 *
 * @throws StateError if a role outside {@link GoogleDriveAiRoles} is present.
 * Roles are constrained by the access's `choices` on the way in, so an
 * unrecognized one is corrupt persisted data, not an input to tolerate:
 * dropping it would silently narrow a sibling's access.
 */
export const maxGoogleDriveAiRole = (
  roles: readonly string[]
): GoogleDriveAiRole | undefined => {
  if (roles.length === 0) return undefined;
  const ranks = roles.map((role) => {
    const rank = GoogleDriveAiRoles.indexOf(role as GoogleDriveAiRole);
    if (rank < 0)
      throw new StateError(
        { role, roles },
        `'${role}' is not a Google Drive role P0 grants.`
      );
    return rank;
  });
  return GoogleDriveAiRoles[Math.max(...ranks)];
};

const SERVICE_ACCOUNT_SUFFIX = ".iam.gserviceaccount.com";

/** Refuses any grantee that is not a Google Cloud service account.
 *
 * Drive ACLs accept `{ type: "user", emailAddress }` for a human exactly as for
 * a service account, so a human address that reached this far would be granted
 * rather than rejected. Only Google can mint an address in the
 * `iam.gserviceaccount.com` namespace, so the suffix is enforced by Google and
 * not by convention.
 *
 * One of four guards on the same hazard, failing at four different times; this
 * is the one that runs on every grant, every revoke, and against every sibling
 * document the lifecycle groups on.
 */
export const assertGranteeAddress = (address: string | undefined): string => {
  if (!address?.endsWith(SERVICE_ACCOUNT_SUFFIX))
    throw new StateError(
      { address },
      "The grantee is not a Google Cloud service account."
    );
  return address;
};
